/* eslint-disable i18next/no-literal-string -- copy is wrapped in t(); disable kept for decorative literals (arrows, ·, ✓). */
import {
  ArrowLeftIcon,
  Button,
  ButtonLink,
  CurrencyInput,
  Select,
  UploadIcon,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { firstDueDateFor, hasBillingAmount, parseBillingCsv, RosterRow, validateRow, WizardRoom } from './types';

interface StepBillingProps {
  stepper?: ReactNode;
  roster: RosterRow[];
  sourceId?: string;
  frequency: RosterRow['billingFrequency'];
  // The org's real rooms, used to label each student's room.
  rooms: WizardRoom[];
  // When true (add-to-billing), every student must have a tuition or subsidy/copay
  // before continuing. When false/absent (add families), billing is optional.
  requireAmount?: boolean;
  onChange: (roster: RosterRow[]) => void;
  onFrequencyChange: (frequency: RosterRow['billingFrequency']) => void;
  onBack: () => void;
  onContinue: () => void;
}

const FREQUENCY_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
];

const PAY_TYPE_OPTIONS = [
  { value: 'private', label: 'Private pay' },
  { value: 'subsidy', label: 'Subsidy' },
];

const WEEKDAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const COLS = 'grid grid-cols-[2fr_1fr_1fr] items-center gap-3';

/**
 * Step 3, Set up billing. The roster is already in; here we set each
 * student's monthly tuition for recurring invoices. Rates are set per room
 * and every student inherits their room's rate (overridable). This is where
 * tuition is required.
 */
export function StepBilling(p: StepBillingProps) {
  const { t } = useTranslation();
  // Label a student's room from the org's real rooms.
  const roomLabel = (id: string) => p.rooms.find((r) => r.id === id)?.name ?? '—';
  // Only students who cleared the roster essentials get a billing row.
  const students = useMemo(
    () => p.roster.filter((r) => !validateRow(r).isBlank && validateRow(r).errors.length === 0),
    [p.roster]
  );

  // Brightwheel & Procare can export their recurring plans; offer to import
  // that file and match plans to students. Other sources set rates by hand.
  const toolLabel = p.sourceId === 'brightwheel' ? 'Brightwheel' : p.sourceId === 'procare' ? 'Procare' : '';
  const canImportBilling = toolLabel !== '';
  const billingFileRef = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSummary, setImportSummary] = useState<{ matched: number; total: number } | null>(null);

  async function handleBillingFile(file: File) {
    setImportError(null);
    try {
      const plans = parseBillingCsv(await file.text());
      if (plans.length === 0) {
        setImportError(t("Couldn't find any billing plans in this file."));
        return;
      }
      const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
      const firstFreq = plans.map((pl) => pl.frequency).find(Boolean);
      if (firstFreq) p.onFrequencyChange(firstFreq as RosterRow['billingFrequency']);
      let matched = 0;
      const next = p.roster.map((r) => {
        const rname = norm(`${r.childFirstName} ${r.childLastName}`);
        const remail = norm(r.parentEmail);
        const hit = plans.find((pl) => (pl.name && norm(pl.name) === rname) || (pl.email && norm(pl.email) === remail));
        if (!hit || !hit.amount) return r;
        matched += 1;
        return {
          ...r,
          billingAmount: hit.amount,
          billingFrequency: (hit.frequency || r.billingFrequency) as RosterRow['billingFrequency'],
        };
      });
      p.onChange(next);
      setImportSummary({ matched, total: students.length });
    } catch (err: any) {
      setImportError(t('Could not read this file: {{error}}', { error: err?.message || err }));
    }
  }

  function onBillingFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleBillingFile(file);
  }

  // Required-field state: we don't nag proactively. Fields are only flagged after
  // the provider tries to continue, and only when a rate is required.
  const [triedContinue, setTriedContinue] = useState(false);

  // Batch controls apply one value to every student; per-student fields override.
  // The provider picks the RECURRING due day — "the 1st" for monthly, "every
  // Tuesday" for weekly/biweekly — which is how schools actually talk about
  // billing. The first actual due date is derived from it (see firstDueDateFor)
  // and shown below the field so the consequence is never something to infer.
  const defaultDueDay = (freq: RosterRow['billingFrequency']) => (freq === 'monthly' ? '1' : 'friday');
  const [dueDay, setDueDay] = useState(() => p.roster.find((r) => r.dueDate)?.dueDate || defaultDueDay(p.frequency));
  const [billFor, setBillFor] = useState(() => p.roster.find((r) => r.billFor)?.billFor || 'next');

  const firstDue = firstDueDateFor(dueDay, p.frequency);

  // With requireAmount on, this screen is a hard gate: every student needs a rate
  // and the due day must be set before Review. The other settings (frequency,
  // bill-for, pay type) always carry a default value.
  const missingCount = students.filter((r) => !hasBillingAmount(r)).length;
  const missingDueDay = !dueDay;
  const hasBlockingErrors = missingCount > 0 || missingDueDay;
  const showRequiredErrors = p.requireAmount && triedContinue;

  function handleContinue() {
    if (p.requireAmount && hasBlockingErrors) {
      setTriedContinue(true);
      return;
    }
    p.onContinue();
  }

  function applyToAll(field: keyof RosterRow, value: string) {
    p.onChange(p.roster.map((r) => ({ ...r, [field]: value })));
  }

  // Monthly offers days 1–28 only: every month has them, so the schedule can't
  // clamp into February and then stay on the 28th forever. Labels use i18next
  // ordinal plurals so they localize instead of hard-coded English suffixes.
  const dayOfMonthOptions = useMemo(
    () =>
      Array.from({ length: 28 }, (_, i) => ({
        value: String(i + 1),
        label: t('day', { count: i + 1, ordinal: true }),
      })),
    [t]
  );
  const dueOptions =
    p.frequency === 'monthly' ? dayOfMonthOptions : WEEKDAY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }));
  // A stale value no longer fits its options when the frequency changes (a
  // day-of-month is meaningless for weekly), so reset to that frequency's
  // default rather than leaving the field invalid.
  useEffect(() => {
    if (!dueOptions.some((o) => o.value === dueDay)) {
      const next = defaultDueDay(p.frequency);
      setDueDay(next);
      applyToAll('dueDate', next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.frequency]);

  function setField(rowId: string, field: keyof RosterRow, value: string) {
    p.onChange(p.roster.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)));
  }

  // The consequence we want to make explicit before continuing: how much we're
  // about to start invoicing, and across how many families.
  const amount = (v: string) => Number((v || '').replace(/[^0-9.]/g, '')) || 0;
  const totalMonthly = students.reduce((sum, r) => sum + amount(r.billingAmount), 0);
  const billedCount = students.filter((r) => hasBillingAmount(r)).length;
  // "Weekly" / "Biweekly" / "Monthly" — drives the amount header + placeholders.
  const freqWord = FREQUENCY_OPTIONS.find((o) => o.value === p.frequency)?.label ?? 'Monthly';
  const freqLabel = freqWord.toLowerCase();
  // "month" / "week" / "period" for the Bill-parent-for toggle labels.
  const servicePeriod = p.frequency === 'monthly' ? t('month') : p.frequency === 'weekly' ? t('week') : t('period');

  // Options with translated labels (values stay stable for matching/logic).
  const frequencyOptions = FREQUENCY_OPTIONS.map((o) => ({ ...o, label: t(o.label) }));
  const payTypeOptions = PAY_TYPE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }));

  return (
    <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6" data-testid="af-step-billing">
      {p.stepper && <div className="border-b border-gray-200 pb-4">{p.stepper}</div>}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{t('addFamilies.billing.title')}</h2>
        <p className="text-muted mt-1 text-sm">
          {p.requireAmount
            ? t(
                "Set a monthly tuition or a subsidy/copay for each family, that's what we'll bill. You can fine-tune any student below."
              )
            : t(
                "This turns on recurring invoices for these families, a separate, deliberate step, set here rather than while adding students. It's optional: anyone without a rate simply won't be invoiced yet."
              )}
        </p>
      </div>

      {canImportBilling && (
        <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-gray-900">
                {t('Import your billing plans from {{tool}}', { tool: toolLabel })}
              </div>
              <p className="text-muted mt-1 text-xs">
                {t(
                  "Export your recurring plans from {{tool}} and drop the CSV here — we'll match each plan to a student and fill in their tuition below.",
                  { tool: toolLabel }
                )}
              </p>
              {importSummary && (
                <div className="mt-1 text-sm font-medium text-green-900">
                  ✓{' '}
                  {t('Matched {{matched}} of {{total}} students. Review below.', {
                    matched: importSummary.matched,
                    total: importSummary.total,
                  })}
                </div>
              )}
            </div>
            <Button
              primary={false}
              size={WidgetSizeEnum.SMALL}
              preIcon={<UploadIcon className="size-5" />}
              onClick={() => billingFileRef.current?.click()}
            >
              {importSummary ? t('Import another file') : t('Upload billing plans')}
            </Button>
          </div>
          <input
            ref={billingFileRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={onBillingFileChange}
            className="hidden"
            data-testid="af-billing-csv"
          />
          {importError && <div className="text-sm text-red-700">{importError}</div>}
        </div>
      )}

      {/* Billing settings — set the defaults for everyone first, then fine-tune
          each student below. Per-student edits win over these. */}
      <div className="space-y-3">
        <h3 className="text-base font-semibold text-gray-900">{t('Billing settings')}</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Select
              label={t('Invoice due date')}
              options={dueOptions}
              size={WidgetSizeEnum.SMALL}
              required={p.requireAmount}
              error={showRequiredErrors && missingDueDay ? t('Select a due date') : undefined}
              value={dueOptions.find((o) => o.value === dueDay) ?? undefined}
              onChange={(opt) => {
                setDueDay(opt.value);
                applyToAll('dueDate', opt.value);
              }}
            />
            {firstDue && (
              <p className="text-muted mt-1.5 text-sm">
                {t('First invoice due {{date}}', { date: dayjs(firstDue).format('dddd, MMMM D, YYYY') })}
              </p>
            )}
          </div>
          <div>
            <Select
              label={t('Billing frequency')}
              options={frequencyOptions}
              size={WidgetSizeEnum.SMALL}
              value={frequencyOptions.find((o) => o.value === p.frequency)}
              onChange={(opt) => p.onFrequencyChange(opt.value as RosterRow['billingFrequency'])}
            />
          </div>
          <div>
            <label className="text-muted mb-1.5 block text-sm font-medium">{t('Bill parent for')}</label>
            <div className="flex overflow-hidden rounded-md border border-blue-900">
              {[
                { value: 'prior', label: t('Previous {{period}} of service', { period: servicePeriod }) },
                { value: 'next', label: t('Upcoming {{period}} of service', { period: servicePeriod }) },
              ].map((opt, i) => {
                const active = billFor === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setBillFor(opt.value as RosterRow['billFor']);
                      applyToAll('billFor', opt.value);
                    }}
                    className={`flex-1 px-3 py-2 text-sm font-semibold ${i === 1 ? 'border-l border-blue-900' : ''} ${
                      active ? 'bg-blue-900 text-white' : 'bg-white text-blue-900'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <p className="text-muted text-xs">{t('Parents are notified 3 days before the due date each period.')}</p>
      </div>

      <div className="border-t border-gray-200" />

      {/* Billing plans — one recurring plan per student; the settings above apply
          to all, per-student payment type and amount are set here. */}
      <h3 className="text-base font-semibold text-gray-900">{t('Billing plans')}</h3>
      <div className="overflow-x-auto">
        <div className="min-w-[640px]">
          <div className={`${COLS} text-muted px-1 pb-1 text-xs font-medium`}>
            <span>{t('Student & payer')}</span>
            <span>{t('Payment type')}</span>
            <span>{t('{{freq}} amount', { freq: freqWord })}</span>
          </div>
          <div className="space-y-1">
            {students.map((row) => {
              const name = (row.childFirstName + ' ' + row.childLastName).trim() || row.parentEmail;
              const rowError = showRequiredErrors && !hasBillingAmount(row);
              const isSubsidy = row.payType === 'subsidy';
              const payer = `${row.parentFirstName} ${row.parentLastName}`.trim();
              return (
                // items-start + a fixed-height amount cell keeps the row from
                // growing when the "Enter an amount" error appears — the input
                // stays put and the message fills reserved space below it.
                <div key={row.id} className="grid grid-cols-[2fr_1fr_1fr] items-start gap-3 rounded-md px-1 pt-1.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">{name}</div>
                    <div className="text-muted truncate text-xs">
                      {roomLabel(row.room)}
                      {payer ? ` · ${payer}` : ''}
                      {row.parentEmail ? ` · ${row.parentEmail}` : ''}
                    </div>
                  </div>
                  <Select
                    options={payTypeOptions}
                    size={WidgetSizeEnum.SMALL}
                    value={payTypeOptions.find((o) => o.value === (row.payType || 'private'))}
                    onChange={(opt) => setField(row.id, 'payType', opt.value as RosterRow['payType'])}
                  />
                  <div className="min-h-14">
                    <CurrencyInput
                      value={row.billingAmount}
                      size={WidgetSizeEnum.SMALL}
                      required={p.requireAmount}
                      error={rowError ? t('Enter an amount') : undefined}
                      placeholder={
                        isSubsidy ? t('{{freq}} copay', { freq: freqWord }) : t('{{freq}} tuition', { freq: freqWord })
                      }
                      onChange={(e) => setField(row.id, 'billingAmount', e.target.value)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Consequence, under the students: how much and how often we'll invoice. */}
      {billedCount > 0 && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-gray-900">
          {t("Starting {{date}}, you'll invoice {{total}} {{frequency}} across {{count}} {{noun}}.", {
            date: firstDue ? dayjs(firstDue).format('MMMM D, YYYY') : t("each student's start date"),
            total: `$${totalMonthly.toLocaleString()}`,
            frequency: t(freqLabel),
            count: billedCount,
            noun: billedCount === 1 ? t('student') : t('students'),
          })}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-gray-200 pt-4">
        <ButtonLink
          content={t('Go back')}
          iconDirection="left"
          icon={<ArrowLeftIcon className="size-4" />}
          onClick={p.onBack}
        />
        <Button primary disabled={students.length === 0} onClick={handleContinue}>
          {t('Continue to review')} →
        </Button>
      </div>
    </section>
  );
}
